import { Text, View } from "react-native";

import { CardTile, cardTileArtBox } from "@/components/brand/CardTile";
import { catalogRarityToBrand } from "@/components/brand/rarity";
import { PICK_CONTROL_SIZE, PickControl } from "@/components/browse/PickControl";
import { Badge, QtyBadge } from "@/components/ui/Badge";
import type { PublicBrowseItem } from "@/hooks/usePublicBrowse";
import { formatCardIdentity, formatGradeSeal } from "@/lib/catalog/cardIdentity";
import { formatCurrency } from "@/lib/money/money";

/** "Matches your want", on a unit that meets one of the signed-in person's Wants (TRO-1931). */
export function WantMatchBadge() {
  return (
    <Badge
      label="Matches your want"
      className="mb-1 bg-accent-soft"
      textClassName="text-primary"
      testID="browse-item-want-match"
    />
  );
}

/** Inset from the art's bottom-right corner, equal on both axes so the control sits in the corner. */
const PICK_INSET = 6;

/**
 * One browsable item as a brand `CardTile` (#413), the customer-browse counterpart of the
 * inventory's `StockGridCard` (#341, ADR-0017).
 *
 * It consumes the narrower `PublicBrowseItem` rather than `StockItem` (no `stock_group_name`, no
 * `cost_basis`, no gain/loss chip) and supplies its own footer: the vendor's price, or "Ask vendor"
 * when there is none. `width` is the cell width the screen computed for its column count.
 *
 * The card's catalog rarity (#755) maps to a brand tier for the same rarity accent as other card
 * grids. A unit the catalog cannot place has no rarity, so `catalogRarityToBrand` returns nothing
 * and the tile draws its neutral border rather than claiming the card is common.
 */
export function PublicGridCard({
  item,
  width,
  onPress,
  quantity,
  onSetQuantity,
  pickPending = false,
  pickLockedReason = null,
  matchesWant = false,
}: {
  item: PublicBrowseItem;
  width: number;
  onPress?: () => void;
  // How many copies the customer has picked (#646), and the setter for the absolute quantity.
  quantity?: number;
  onSetQuantity?: (next: number) => void;
  pickPending?: boolean;
  /**
   * Why picking is unavailable on the whole storefront, or `null` while it is open (TRO-673).
   *
   * Set once the customer's list is with the Vendor. `PickControl` greys the control and speaks
   * the reason.
   */
  pickLockedReason?: string | null;
  /** Whether this unit meets one of the signed-in person's Wants; the caller decides. */
  matchesWant?: boolean;
}) {
  const seal = formatGradeSeal(item.grading_company, item.grade, item.is_graded);

  const descriptor = seal?.label
    ?? (item.product_type === "sealed" ? "sealed" : item.condition)
    ?? null;
  // Set and collector number (#530) tell two same-name cells apart. The tile renders its own copy;
  // this one is for the spoken label.
  const identity = formatCardIdentity(item.set_name, item.number, item.variant_label);
  const a11yLabel = [
    item.product_name,
    identity,
    descriptor,
    item.qty > 1 ? `${item.qty} available` : null,
    item.price != null ? formatCurrency(item.price, item.currency) : "ask vendor for price",
    matchesWant ? "matches your want" : null,
  ].filter(Boolean).join(", ");
  const art = cardTileArtBox(width);

  return (
    <View style={{ width }}>
      <CardTile
        name={item.product_name}
        set={item.set_name ?? undefined}
        number={item.number ?? undefined}
        variant={item.variant_label ?? undefined}
        // `undefined`, not "common", when the payload has no rarity, so the tile draws its neutral
        // border (#755).
        rarity={catalogRarityToBrand(item.rarity)}
        seal={seal ?? undefined}
        image={item.image_url ?? undefined}
        width={width}
        onPress={onPress}
        accessibilityLabel={a11yLabel}
        testIDs={{
          tile: "browse-item-row",
          name: "browse-item-name",
          identity: "browse-item-identity",
        }}
        badge={item.qty > 1 ? <QtyBadge qty={item.qty} /> : undefined}
        // Left-align the footer under the name, which follows `CardTile`'s left-aligned Title rule,
        // so the price and name share one axis.
        footer={
          <View className="mt-1 items-start">
            {matchesWant ? <WantMatchBadge /> : null}
            {!seal && descriptor
              ? <Text className="text-xs font-body text-muted-foreground">{descriptor}</Text>
              : null}
            {item.price != null
              ? (
                <Text
                  testID="browse-item-price"
                  className="font-num-bold text-sm text-primary"
                  numberOfLines={1}
                >
                  {formatCurrency(item.price, item.currency)}
                </Text>
              )
              : (
                // A null price is not free; the customer has to ask, so say that, as the list row
                // and detail page do (#469). `font-label` is already a bold cut, and a weight
                // utility on top does nothing in RN (DESIGN.md §6).
                <Text
                  testID="browse-item-price"
                  className="text-[11px] font-label text-muted-foreground"
                  numberOfLines={1}
                >
                  Ask vendor
                </Text>
              )}
          </View>
        }
      />
      {
        /* The pick control sits over the art's bottom-right corner as a sibling of the tile
           Pressable, not a child, because nested Pressables double the tap and screen-reader focus
           target (#469). It is positioned from `cardTileArtBox`, so a tap on it never also opens
           the detail page, and it avoids the top-right, where the qty badge sits. */
      }
      {onSetQuantity
        ? (
          <View
            style={{
              position: "absolute",
              top: art.top + art.height - PICK_CONTROL_SIZE - PICK_INSET,
              right: art.left + PICK_INSET,
            }}
          >
            <PickControl
              productName={item.product_name}
              quantity={quantity ?? 0}
              available={item.qty}
              onChange={onSetQuantity}
              overlay
              disabled={pickPending}
              lockedReason={pickLockedReason}
            />
          </View>
        )
        : null}
    </View>
  );
}
